import React, { createContext, useContext, useEffect, useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const DEFAULT_PRICING = {
  individual: 15,
  packFirst: 35,
  packAdditional: 20,
  currency: 'eur',
  taxRate: 7,
  taxesIncludedInPrices: false,
} as const;

export interface PricingConfig {
  individual: number;
  packFirst: number;
  packAdditional: number;
  currency: string;
  taxRate: number;
  taxesIncludedInPrices: boolean;
  loading: boolean;
}

const PricingContext = createContext<PricingConfig | undefined>(undefined);

function mapApiPricing(data: Record<string, unknown>): Omit<PricingConfig, 'loading'> {
  return {
    individual: Number(data.individual_eur) || DEFAULT_PRICING.individual,
    packFirst: Number(data.pack_first_eur) || DEFAULT_PRICING.packFirst,
    packAdditional: Number(data.pack_additional_eur) || DEFAULT_PRICING.packAdditional,
    currency: typeof data.currency === 'string' ? data.currency : DEFAULT_PRICING.currency,
    taxRate: Number(data.tax_rate) || DEFAULT_PRICING.taxRate,
    taxesIncludedInPrices: data.taxes_included_in_prices === true,
  };
}

export const PricingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pricing, setPricing] = useState<Omit<PricingConfig, 'loading'>>(DEFAULT_PRICING);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/site/pricing`, { cache: 'no-store' });
        if (!res.ok) throw new Error(`pricing ${res.status}`);
        const data = (await res.json()) as Record<string, unknown>;
        if (!cancelled) setPricing(mapApiPricing(data));
      } catch {
        if (!cancelled) setPricing(DEFAULT_PRICING);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <PricingContext.Provider value={{ ...pricing, loading }}>
      {children}
    </PricingContext.Provider>
  );
};

export const usePricing = (): PricingConfig => {
  const context = useContext(PricingContext);
  if (context === undefined) {
    throw new Error('usePricing must be used within a PricingProvider');
  }
  return context;
};
